// The agent's harness section (ADR 0039, the manifest's `harness`), shown where the guide's loop puts each part
// (§2, §43): the history it sees in Context; permissions, other agents and tool answers in Tools; checks in
// Guardrails & Verification; how it runs and its budgets in Budgets (in tokens — a cost budget only where a host
// prices its models and one is set); tracing and the effective harness, with the source of every value, in Runs.
// A project agent's section is its own; a platform agent's is read-only (a project that still overrides one edits
// that file). Every edit is previewed by the api (validated there, never in two places) before it is saved; each
// tab saves its own edits as one commit.
import { cn } from '@cortex/ui';
import type { HarnessView } from '@cortex/sdk-agents-client';
import { FlaskConical, Loader2, Lock, RotateCcw, Save, Split, Trash2 } from 'lucide-react';
import * as React from 'react';
import { agentRunsKey, useAgentsHost, useAgentsQuery, useInvalidate, useAgentsKey } from './host.tsx';
import type { AgentTabContext } from './tabs.tsx';
import type { PermissionCells } from './ToolsTab.tsx';
import { btnGhost, btnPrimary, input, messageOf, SwitchTrack } from './ui.tsx';

type Draft = Record<string, unknown>;

const get = (o: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((v, k) => (v && typeof v === 'object' ? (v as Draft)[k] : undefined), o);

/** A copy of `o` with `path` set (undefined removes it, and empty parents with it). */
function setPath(o: Draft, path: string, value: unknown): Draft {
  const [k, ...rest] = path.split('.');
  const out = { ...o };
  if (!rest.length) {
    if (value === undefined) delete out[k!];
    else out[k!] = value;
    return out;
  }
  const child = setPath((out[k!] as Draft | undefined) ?? {}, rest.join('.'), value);
  if (Object.keys(child).length) out[k!] = child;
  else delete out[k!];
  return out;
}

const SOURCE_LABEL: Record<string, string> = {
  platform: 'Platform default',
  persona: 'Platform agent',
  project: 'This project',
  run: 'This run',
};

/** A field's source next to its value, in the words of the form. */
const FIELD_SOURCE: Record<string, string> = {
  platform: 'default',
  persona: 'platform agent',
  project: 'set for this agent',
  run: 'this run',
};

/** The settings a field edits, with the words the form shows. */
interface FieldDef {
  path: string;
  label: string;
  help: string;
  kind: 'number' | 'select' | 'check';
  /** number: empty means none (no limit, or the host's setting). */
  empty?: string;
  scale?: number;
  unit?: string;
  options?: { value: string; label: string }[];
}

/** The fields of the sections that are a list of settings (Budgets, and the rows of Advanced settings), by group. */
const FIELDS: Record<string, FieldDef[]> = {
  limits: [
    {
      path: 'budgets.turns',
      label: 'Steps',
      help: 'Model calls per run.',
      kind: 'number',
      empty: '12',
    },
    {
      path: 'budgets.tool_calls',
      label: 'Tool calls',
      help: 'Per run — the only tool-call limit.',
      kind: 'number',
      empty: 'No limit',
    },
    {
      path: 'budgets.time_ms',
      label: 'Time',
      help: 'Seconds per run.',
      kind: 'number',
      empty: 'No limit',
      scale: 1000,
      unit: 's',
    },
    {
      path: 'budgets.tokens',
      label: 'Tokens',
      help: 'Read and written, all steps together.',
      kind: 'number',
      empty: 'No limit',
    },
    {
      path: 'budgets.delegations',
      label: 'Calls to other agents',
      help: 'Per answer (May call other agents is in Tools).',
      kind: 'number',
    },
    {
      path: 'budgets.cost_usd',
      label: 'Cost',
      help: 'US dollars per run; counted only where the host prices its models.',
      kind: 'number',
      empty: 'No limit',
      unit: '$',
    },
  ],
  answers: [
    {
      path: 'tools.result.max_chars',
      label: 'Characters read from an answer',
      help: 'The agent sees only the start of a longer answer.',
      kind: 'number',
    },
    {
      path: 'tools.result.mode',
      label: 'Answers',
      help: 'Short reads at most 1,500 characters.',
      kind: 'select',
      options: [
        { value: 'verbose', label: 'Full' },
        { value: 'short', label: 'Short' },
      ],
    },
    {
      path: 'tools.result.paging_hint',
      label: 'Say when an answer was cut',
      help: 'The agent learns it saw only part and can ask for less; otherwise it thinks it saw everything.',
      kind: 'check',
    },
    {
      path: 'tools.errors',
      label: 'Errors',
      help: 'Structured: what failed, whether to try again, whether anything changed.',
      kind: 'select',
      options: [
        { value: 'text', label: 'Plain text' },
        { value: 'structured', label: 'Structured' },
      ],
    },
    {
      path: 'tools.retry.max_attempts',
      label: 'Tries on an error',
      help: 'Of a read or a tool marked safe to retry, when it fails for a passing reason (a timeout, a busy service).',
      kind: 'number',
    },
    {
      path: 'tools.retry.backoff_ms',
      label: 'Wait between tries',
      help: 'Milliseconds.',
      kind: 'number',
      unit: 'ms',
    },
  ],
  memory: [
    {
      path: 'context.history.messages',
      label: 'Messages it sees',
      help: 'Earlier messages of the chat it reads; all of them stay in Runs.',
      kind: 'number',
    },
    {
      path: 'context.history.message_chars',
      label: 'Characters per message',
      help: 'Each earlier message is cut at this length.',
      kind: 'number',
    },
    {
      path: 'context.history.delegated_answer_chars',
      label: 'Characters per helper answer',
      help: 'Each earlier answer of another agent is cut at this length.',
      kind: 'number',
    },
    {
      path: 'context.prompt_caching',
      label: 'Prompt caching',
      help: 'Reuse the instructions between steps at the model provider (faster, cheaper).',
      kind: 'select',
      options: [
        { value: 'default', label: 'Provider default' },
        { value: 'on', label: 'On' },
        { value: 'off', label: 'Off' },
      ],
    },
  ],
  protection: [
    {
      path: 'provenance.mark_untrusted',
      label: 'Protection from instructions in data',
      help: 'Read tool answers as data that may contain instructions to ignore. Turn it on if the agent reads other people’s data (emails, documents, websites).',
      kind: 'check',
    },
  ],
  agents: [
    {
      path: 'subagents.allowed',
      label: 'May call other agents',
      help: 'Off: it works alone.',
      kind: 'check',
    },
  ],
  tracing: [
    {
      path: 'observability.span_names',
      label: 'Names in tracing',
      help: 'OpenTelemetry GenAI names for tracing tools that read them.',
      kind: 'select',
      options: [
        { value: 'cortex', label: 'Platform' },
        { value: 'gen_ai', label: 'OpenTelemetry GenAI' },
      ],
    },
    {
      path: 'observability.genai_attributes',
      label: 'Add GenAI attributes',
      help: 'Agent name and operation on each trace.',
      kind: 'check',
    },
  ],
};

const POLICY_OPTIONS = [
  { value: 'allow', label: 'Allow' },
  { value: 'approve', label: 'Ask first' },
  { value: 'deny', label: 'Never' },
];

/** A number field as wide as a number, without the browser's arrows. */
const number = `${input.replace('w-full', 'w-36')} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

/** A select as wide as its words (the form's input fills its cell). */
const narrow = input.replace('w-full', 'w-auto');

const showValue = (v: unknown) =>
  v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const fmtK = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString());

/** What the agent's recent runs needed (its Runs: the ledger), to pick a limit that does not cut normal work. */
interface Usage {
  runs: number;
  calls: Record<string, number>;
  maxToolCalls: number;
  maxMs: number;
  maxTokens: number | null;
}

function usageOf(runs: unknown[]): Usage {
  const u: Usage = { runs: runs.length, calls: {}, maxToolCalls: 0, maxMs: 0, maxTokens: null };
  for (const r of runs as Record<string, unknown>[]) {
    const calls = Array.isArray(r.toolCalls) ? (r.toolCalls as Record<string, unknown>[]) : [];
    for (const c of calls) if (typeof c?.name === 'string') u.calls[c.name] = (u.calls[c.name] ?? 0) + 1;
    u.maxToolCalls = Math.max(u.maxToolCalls, calls.length);
    u.maxMs = Math.max(u.maxMs, num(r.durationMs) ?? 0);
    const t = (num(r.inputTokens) ?? 0) + (num(r.outputTokens) ?? 0);
    if (num(r.inputTokens) !== null) u.maxTokens = Math.max(u.maxTokens ?? 0, t);
  }
  return u;
}

/** The parts of the agent's harness section a tab shows (each tab saves its own edits: one commit). */
export type HarnessPart =
  'mode' | 'budgets' | 'policy' | 'other-agents' | 'answers' | 'history' | 'checks' | 'developers';

/**
 * Parts of the agent's harness section, as a tab shows them (Tools, Context, Guardrails & Verification, Budgets, Runs).
 * `tools` draws the agent's tools table with each tool's permission in it (the Tools tab: one row per tool) in place of
 * a Permissions card of its own; it is drawn without the permissions while the harness is read.
 */
export function HarnessSections({
  agent,
  parts,
  tools,
}: AgentTabContext & {
  parts: HarnessPart[];
  tools?: (permissions: PermissionCells | null) => React.ReactNode;
}) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  const key = k('harness', agent.subject);
  const saved = useAgentsQuery(key, () => host.client.harness(agent.subject));
  // Which tool reads and which writes, and which is an app's (a project agent's manifest).
  const manifest = useAgentsQuery(k('manifest', agent.key), () => host.client.manifest(agent.key), {
    enabled: agent.kind === 'project',
  });
  const recent = useAgentsQuery(
    agentRunsKey(
      host.cache.runs,
      agent.kind === 'persona' ? agent.key : `agent-${agent.key}`,
      'harness-usage',
    ),
    () =>
      host.runs!({
        persona: agent.kind === 'persona' ? agent.key : `agent-${agent.key}`,
        all: true,
        limit: 50,
      }),
    { enabled: !!host.runs },
  );
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [preview, setPreview] = React.useState<HarnessView | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');
  const [onlyChanged, setOnlyChanged] = React.useState(true);

  // Every edit is previewed by the api: validated there, with the effective harness and its sources.
  React.useEffect(() => {
    if (draft === null) {
      setPreview(null);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      host.client
        .previewHarness(agent.subject, Object.keys(draft).length ? draft : null)
        .then((v) => live && setPreview(v))
        .catch((e: unknown) => live && setError(messageOf(e)));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [draft, host, agent.subject]);

  const withTools = tools && parts.includes('policy');
  if (saved.loading && !saved.data)
    return withTools ? (
      tools(null)
    ) : (
      <div className="flex items-center gap-2 text-sm text-page-muted">
        <Loader2 size={14} className="animate-spin" /> Reading the harness…
      </div>
    );
  if (saved.error)
    return (
      <>
        {withTools ? tools(null) : null}
        <div className="text-sm text-red-600">{saved.error}</div>
      </>
    );
  const view = preview ?? saved.data!;
  const editing = draft !== null;
  const eff = view.effective.harness;
  const own = (editing ? draft : (view.own ?? {})) as Draft;
  const persona = view.kind === 'persona';
  const problemsOf = (path: string) => view.problems.filter((p) => p.startsWith(`harness.${path}`));
  // Whether each tool changes data: a project agent's from its manifest, a platform agent's from its catalog entry.
  const access = new Map(
    persona
      ? agent.tools.map((t) => [t.name, { write: t.access === 'write', app: t.source === 'app' }])
      : (manifest.data?.tools ?? []).map((t) => [
          t.name,
          { write: t.operation.access === 'write', app: t.operation.source === 'app' },
        ]),
  );
  const usage = recent.data ? usageOf(recent.data as unknown[]) : null;

  // A project agent's fields are open at once: the first change starts the draft (a platform agent's wait for
  // "Override in this project").
  const open = editing || (!persona && view.editable);
  const set = (path: string, value: unknown) =>
    setDraft((d) => setPath(d ?? (JSON.parse(JSON.stringify(view.own ?? {})) as Draft), path, value));
  const start = () => {
    setNote('');
    setError('');
    setDraft(JSON.parse(JSON.stringify(view.own ?? {})) as Draft);
  };
  const discard = () => {
    setDraft(null);
    setError('');
  };
  const save = async (harness: Draft | null) => {
    setBusy(true);
    setError('');
    try {
      await host.client.saveHarness(agent.subject, harness && Object.keys(harness).length ? harness : null);
      setDraft(null);
      setNote(`Saved as one change. The next run uses it.`);
      invalidate([key, k()]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const runEvals = async () => {
    if (!host.evals) return;
    setBusy(true);
    setError('');
    try {
      const suites = await host.evals.suites();
      const keys = suites
        .filter((s) => s.subject === agent.subject)
        .flatMap((s) => s.cases.filter((c) => c.enabled !== false).map((c) => c.key));
      if (!keys.length) {
        setNote('This agent has no evaluations here yet: add them in the Evaluations tab.');
        return;
      }
      const harness = editing ? draft : null;
      const run = await host.evals.start({
        keys,
        ...(harness && Object.keys(harness).length ? { harness } : {}),
      });
      setNote(`Evaluation run #${run.id} started with this harness. Open the Evaluations tab to follow it.`);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const sourceOf = (path: string) => view.effective.sources[path] ?? 'platform';
  const changed = new Set(view.diff.map((d) => d.path));
  const unsaved = editing
    ? new Set(
        [...flatten(draft), ...flatten(saved.data!.own ?? {})]
          .map(([p]) => p)
          .filter((p) => p && showValue(get(draft, p)) !== showValue(get(saved.data!.own ?? {}, p))),
      ).size
    : 0;
  const field = (f: FieldDef, extra?: React.ReactNode) => (
    <Field
      key={f.path}
      f={f}
      own={get(own, f.path)}
      effective={get(eff, f.path)}
      editing={open}
      source={sourceOf(f.path)}
      changed={changed.has(f.path)}
      problems={problemsOf(f.path)}
      onChange={(v) => set(f.path, v)}
      {...(extra !== undefined ? { extra } : {})}
    />
  );

  // The summary: each section in one sentence, in plain words, from the effective harness (unsaved edits included).
  const policyOf = (t: string) =>
    String(get(eff, `policy.tools.${t}`) ?? get(eff, 'policy.default') ?? 'allow');
  // The agent's older tool-call limit (guardrails.max_tool_calls) still holds; Budgets shows it as the one limit.
  const guardCalls = num((agent.guardrails as Draft | null)?.max_tool_calls);
  // Cost is counted only where the host prices its models: shown when one is set, never asked for.
  const costSet = num(get(eff, 'budgets.cost_usd')) !== null;
  const show = (p: HarnessPart) => parts.includes(p);
  // The save bar of these parts: unsaved edits, Discard, a run of the evaluations with them, Save.
  const bar =
    editing || (persona && view.overridden && view.editable) ? (
      <div className="flex flex-wrap items-center gap-2 sticky bottom-0 bg-page-bg py-2 border-t border-card-border">
        {editing ? (
          <span className="text-xs text-page-muted" data-testid="harness-unsaved">
            {unsaved ? `${unsaved} change${unsaved === 1 ? '' : 's'}, not saved` : 'No changes yet'}
          </span>
        ) : null}
        <span className="flex-1" />
        {editing ? (
          <button
            type="button"
            className={btnGhost}
            data-testid="harness-discard"
            onClick={discard}
            disabled={busy}
          >
            <RotateCcw size={14} /> Discard
          </button>
        ) : null}
        {persona && view.overridden && view.editable && !editing ? (
          <button
            type="button"
            className={btnGhost}
            data-testid="harness-remove-override"
            disabled={busy}
            onClick={() => void save(null)}
          >
            <Trash2 size={14} /> Remove the override
          </button>
        ) : null}
        {host.evals && editing ? (
          <button
            type="button"
            className={btnGhost}
            data-testid="harness-run-evals"
            disabled={busy || view.problems.length > 0}
            onClick={() => void runEvals()}
            title="Run this agent’s evaluations with these changes before saving them"
          >
            <FlaskConical size={14} /> Test with evaluations
          </button>
        ) : null}
        {editing ? (
          <button
            type="button"
            className={btnPrimary}
            data-testid="harness-save"
            disabled={busy || view.problems.length > 0}
            onClick={() => void save(draft)}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        ) : null}
      </div>
    ) : null;

  // Each tool's permission: a choice while the harness is open to edit, else the permission in words.
  const policyLabel = (v: string) => POLICY_OPTIONS.find((o) => o.value === v)?.label ?? v;
  const permissionOf = (t: string) => {
    const path = `policy.tools.${t}`;
    const value = get(own, path) ?? get(eff, path);
    const a = access.get(t);
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {open ? (
          <select
            className={narrow}
            data-testid={`harness-policy-${t}`}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) =>
              set(path, e.target.value && e.target.value !== policyOf('*') ? e.target.value : undefined)
            }
          >
            <option value="">{policyLabel(policyOf('*'))} · default</option>
            {POLICY_OPTIONS.filter((o) => o.value !== policyOf('*')).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-page-text whitespace-nowrap" data-testid={`harness-policy-${t}`}>
            {policyLabel(policyOf(t))}
            {typeof value === 'string' ? '' : <span className="text-page-muted"> · default</span>}
          </span>
        )}
        {a?.write && policyOf(t) === 'allow' ? (
          <span className="text-[11px] text-amber-700 dark:text-amber-400">
            suggested “Ask first”: it writes data
          </span>
        ) : null}
      </span>
    );
  };
  const everyOther = (
    <div data-testid="harness-section-policy">
      <div className="flex flex-wrap items-center gap-2">
        <span>Every other tool:</span>
        {open ? (
          <select
            className={narrow}
            data-testid="harness-policy-default"
            value={String(get(own, 'policy.default') ?? get(eff, 'policy.default'))}
            onChange={(e) => set('policy.default', e.target.value === 'allow' ? undefined : e.target.value)}
          >
            {POLICY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-page-text" data-testid="harness-policy-default">
            {policyLabel(policyOf('*'))}
          </span>
        )}
        <span>
          · “Ask first”: the run stops at that call and waits for the OK of the person who runs it, in the
          chat.
        </span>
      </div>
      <div className="mt-1" data-testid="harness-rights">
        The agent works with the rights of the user who runs it: it cannot do more than that user can.
      </div>
      {problemsOf('policy').map((p) => (
        <Problem key={p} text={p} />
      ))}
    </div>
  );
  const cells: PermissionCells = {
    cell: permissionOf,
    callsHead: usage ? `Calls, last ${usage.runs} run${usage.runs === 1 ? '' : 's'}` : null,
    calls: (t) => `${usage?.calls[t] ?? 0}×`,
    footer: everyOther,
  };

  return (
    <div className="flex flex-col gap-3" data-testid={`harness-parts-${parts.join('-')}`}>
      {persona && view.overridden && !editing ? (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border border-card-border bg-page-bg px-3 py-2 text-sm"
          data-testid="harness-readonly"
        >
          <Lock size={14} className="text-page-muted" />
          <span className="flex-1 min-w-[240px]">
            This project overrides the platform agent’s settings in {view.path}.
          </span>
          {view.editable ? (
            <button type="button" className={btnGhost} data-testid="harness-override" onClick={start}>
              <Split size={14} /> Edit the override
            </button>
          ) : null}
        </div>
      ) : null}

      {show('mode') ? (
        <Card title="How it runs" testId="harness-section-mode">
          <div className="grid gap-2 md:grid-cols-2">
            {(
              [
                {
                  value: 'interactive',
                  label: '⚡ Interactive',
                  help: 'Answers within one request, in the chat.',
                },
                {
                  value: 'long_running',
                  label: 'Long-running',
                  help: 'Works in the background for hours, survives a restart, can wait for a person’s OK.',
                },
              ] as const
            ).map((m) => {
              const disabled = !open || (m.value === 'long_running' && !view.longRunning);
              const checked = (get(eff, 'mode') ?? 'interactive') === m.value;
              return (
                <label
                  key={m.value}
                  data-testid={`harness-mode-${m.value}`}
                  className={cn(
                    'flex gap-2 rounded-md border px-3 py-2 text-sm',
                    checked ? 'border-nav-active' : 'border-card-border',
                    disabled ? 'opacity-70' : 'cursor-pointer',
                  )}
                >
                  <input
                    type="radio"
                    name="harness-mode"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => set('mode', m.value === 'interactive' ? undefined : m.value)}
                  />
                  <span>
                    <span className="font-medium text-page-text">{m.label}</span>
                    <span className="block text-xs text-page-muted mt-0.5">
                      {m.help}
                      {m.value === 'long_running' && !view.longRunning
                        ? persona
                          ? ' Platform agents always run inside a chat turn.'
                          : ' Not available on this platform yet.'
                        : ''}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </Card>
      ) : null}

      {show('budgets') ? (
        <Card title="Budgets" sub="per run" testId="harness-section-budgets">
          <div className="grid grid-cols-[minmax(140px,1fr)_minmax(160px,1fr)_auto_auto] gap-x-3 gap-y-1.5 items-center text-sm">
            <span className="text-xs text-page-muted">Budget</span>
            <span className="text-xs text-page-muted">Value</span>
            <span />
            <span className="text-xs text-page-muted">{usage ? `Max, last ${usage.runs} runs` : ''}</span>
            {FIELDS.limits!.filter((f) => f.path !== 'budgets.cost_usd' || costSet)
              .filter((f) => f.path !== 'budgets.delegations' || get(eff, 'subagents.allowed') !== false)
              .map((f) =>
                field(
                  f.path === 'budgets.tool_calls' && guardCalls !== null
                    ? { ...f, empty: `${guardCalls} · from guardrails` }
                    : f,
                  usage ? (
                    <span className="text-xs text-page-muted">
                      {f.path === 'budgets.tool_calls'
                        ? usage.maxToolCalls
                        : f.path === 'budgets.time_ms'
                          ? `${Math.round(usage.maxMs / 1000)} s`
                          : f.path === 'budgets.tokens'
                            ? usage.maxTokens === null
                              ? '—'
                              : fmtK(usage.maxTokens)
                            : '—'}
                    </span>
                  ) : (
                    <span />
                  ),
                ),
              )}
          </div>
        </Card>
      ) : null}

      {show('policy') && tools ? tools(cells) : null}

      {show('other-agents') ? (
        <Card title="Other agents" sub="handing part of the work on" testId="harness-section-agents">
          <Grid>{FIELDS.agents!.map((f) => field(f))}</Grid>
          <div className="text-xs text-page-muted mt-1">How many per answer — in Budgets.</div>
        </Card>
      ) : null}

      {show('answers') ? (
        <Card title="Tool answers" sub="what comes back to the model" testId="harness-section-tools">
          <Grid>{FIELDS.answers!.map((f) => field(f))}</Grid>
          <div className="mt-2 text-xs text-page-muted">
            Safe to retry (a retry cannot repeat a side effect):
          </div>
          <div className="flex flex-wrap gap-3 mt-1">
            {view.tools.map((t) => {
              const on =
                get(own, `tools.overrides.${t}.idempotent`) === true ||
                get(eff, `tools.overrides.${t}.idempotent`) === true;
              return (
                <label
                  key={t}
                  className={cn(
                    'text-xs text-page-text flex items-center gap-2 font-mono',
                    open ? 'cursor-pointer' : '',
                  )}
                >
                  <input
                    type="checkbox"
                    role="switch"
                    className="sr-only"
                    data-testid={`harness-idempotent-${t}`}
                    disabled={!open}
                    checked={on}
                    onChange={(e) =>
                      set(`tools.overrides.${t}.idempotent`, e.target.checked ? true : undefined)
                    }
                  />
                  <SwitchTrack on={on} dim={!open} />
                  {t}
                </label>
              );
            })}
          </div>
          <div className="mt-3 border-t border-divider pt-2" data-testid="harness-section-safety">
            <Grid>{FIELDS.protection!.map((f) => field(f))}</Grid>
          </div>
        </Card>
      ) : null}

      {show('history') ? (
        <Card title="History it sees" sub="older chat messages are not sent" testId="harness-section-context">
          <Grid>{FIELDS.memory!.map((f) => field(f))}</Grid>
        </Card>
      ) : null}

      {show('checks') ? (
        <Card n={5} title="Checks" sub="how to tell a good answer" testId="harness-section-verification">
          <div className="-mt-1 mb-2 text-xs text-page-muted">
            A failed check marks the run “Needs a person”. All passed: “Done, verified”. Guardrails that pass
            count too.
          </div>
          <SimpleChecks
            checks={(get(own, 'verification.checks') ?? get(eff, 'verification.checks') ?? []) as Draft[]}
            tools={view.tools}
            suggested={view.tools.filter((t) => access.get(t)?.app && !access.get(t)?.write)}
            editing={open}
            onChecks={(c) => set('verification.checks', c.length ? c : undefined)}
          />
          <EndStateChecks
            checks={
              (get(own, 'verification.end_state') ?? get(eff, 'verification.end_state') ?? []) as Draft[]
            }
            editing={open}
            onChecks={(c) => set('verification.end_state', c.length ? c : undefined)}
          />
          {problemsOf('verification').map((p) => (
            <Problem key={p} text={p} />
          ))}
        </Card>
      ) : null}

      {show('developers') ? (
        <MoreRow
          title="For developers"
          summary={`tracing — ${get(eff, 'observability.span_names') === 'gen_ai' ? 'OpenTelemetry GenAI' : 'platform'} names · version ${view.effective.hash}`}
          dev
          hint="How runs are named in tracing, and the effective harness: every value, where it comes from, what this agent changes."
          testId="harness-section-observability"
        >
          <Grid>{FIELDS.tracing!.map((f) => field(f))}</Grid>
          <div className="flex flex-wrap items-center gap-2 text-xs mt-3" data-testid="harness-layers">
            {view.effective.layers.map((l, i) => (
              <React.Fragment key={`${l.kind}-${i}`}>
                {i ? <span className="text-page-muted">→</span> : null}
                <span className="rounded border border-card-border px-2 py-0.5" data-layer={l.kind}>
                  {SOURCE_LABEL[l.kind] ?? l.kind}
                  {l.kind !== 'platform' ? <span className="text-page-muted"> · {l.label}</span> : null}
                </span>
              </React.Fragment>
            ))}
            <span className="ml-auto text-page-muted font-mono" data-testid="harness-hash">
              version {view.effective.hash}
            </span>
          </div>
          <label className="text-xs text-page-muted flex items-center gap-1 my-2">
            <input
              type="checkbox"
              checked={onlyChanged}
              onChange={(e) => setOnlyChanged(e.target.checked)}
              data-testid="harness-only-changed"
            />
            Only values that differ from the platform default
          </label>
          <EffectiveTable view={view} onlyChanged={onlyChanged} />
          <div className="mt-2 text-xs text-page-muted" data-testid="harness-diff">
            {view.diff.length
              ? `This agent changes ${view.diff.length} value${view.diff.length === 1 ? '' : 's'} over what it inherits: ${view.diff.map((d) => `${d.path} ${showValue(d.a)} → ${showValue(d.b)}`).join('; ')}.`
              : 'Nothing is changed over what it inherits.'}
          </div>
        </MoreRow>
      ) : null}

      {error ? (
        <div className="text-sm text-red-600" data-testid="harness-error">
          {error}
        </div>
      ) : null}
      {view.problems.length ? (
        <div className="text-sm text-red-600" data-testid="harness-problems">
          {view.problems.join('; ')}
        </div>
      ) : null}
      {note ? (
        <div className="text-sm text-page-muted" data-testid="harness-note">
          {note}
        </div>
      ) : null}
      {bar}
    </div>
  );
}

/** A section's number: a gray digit, as the views number steps (the summary's lines name the same numbers). */
const Num = ({ n }: { n: number }) => (
  <span className="shrink-0 text-xs font-medium text-page-muted">{n}</span>
);

const Grid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-[minmax(160px,1fr)_minmax(180px,1fr)_auto] gap-x-3 gap-y-1.5 items-center text-sm">
    {children}
  </div>
);

/** A row of Advanced settings: what it is and how it is now, opened for its explanation and fields. */
function MoreRow({
  title,
  summary,
  hint,
  warn,
  dev,
  testId,
  children,
}: {
  title: string;
  summary: string;
  hint: string;
  warn?: boolean;
  dev?: boolean;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <details
      className={cn(
        'rounded-md border px-3 py-2 mb-1.5',
        dev ? 'border-dashed border-card-border' : 'border-card-border',
      )}
      data-testid={testId}
    >
      <summary className="cursor-pointer text-sm">
        {dev ? (
          <span className="text-page-muted">
            {title}: {summary}
          </span>
        ) : (
          <>
            <b className="text-page-text">{title}</b>{' '}
            <span className={warn ? 'text-amber-700 dark:text-amber-300' : 'text-page-muted'}>
              — {summary}
            </span>
          </>
        )}
      </summary>
      <div className="text-xs text-page-muted my-2">{hint}</div>
      {children}
    </details>
  );
}

function Card({
  n,
  title,
  sub,
  action,
  testId,
  children,
}: {
  n?: number;
  title: string;
  sub?: string;
  /** At the right of the title (the summary: Edit). */
  action?: React.ReactNode;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-card-border bg-card-bg p-4 scroll-mt-4" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {n ? <Num n={n} /> : null}
        <h3 className="m-0 text-[15px] font-semibold text-page-title">{title}</h3>
        {sub ? <span className="text-xs text-page-muted">— {sub}</span> : null}
        {action ? <span className="ml-auto">{action}</span> : null}
      </div>
      {children}
    </section>
  );
}

function SourceTag({ source, changed }: { source: string; changed: boolean }) {
  return (
    <span
      className={cn(
        'text-[11px] whitespace-nowrap',
        changed ? 'text-nav-active font-medium' : 'text-page-muted',
      )}
      data-source={source}
    >
      {FIELD_SOURCE[source] ?? source}
    </span>
  );
}

const Problem = ({ text }: { text: string }) => (
  <div className="text-xs text-red-600 mt-1" data-testid="harness-field-problem">
    {text.replace(/^harness\./, '')}
  </div>
);

function Field({
  f,
  own,
  effective,
  editing,
  source,
  changed,
  problems,
  onChange,
  extra,
}: {
  f: FieldDef;
  own: unknown;
  effective: unknown;
  editing: boolean;
  source: string;
  changed: boolean;
  problems: string[];
  onChange(v: unknown): void;
  /** A fourth cell after the source (Limits: what the agent's runs needed). */
  extra?: React.ReactNode;
}) {
  const id = `harness-field-${f.path.replace(/\./g, '-')}`;
  const value = own ?? effective;
  let control: React.ReactNode;
  if (f.kind === 'check')
    control = (
      <label className={cn('inline-flex', editing ? 'cursor-pointer' : '')}>
        <input
          type="checkbox"
          role="switch"
          className="sr-only"
          data-testid={id}
          disabled={!editing}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        <SwitchTrack on={value === true} dim={!editing} />
      </label>
    );
  else if (f.kind === 'select')
    control = (
      <select
        className={narrow}
        data-testid={id}
        disabled={!editing}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      >
        {f.options!.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  else {
    const scale = f.scale ?? 1;
    const shown = typeof value === 'number' ? String(value / scale) : '';
    control = (
      <span className="inline-flex items-center gap-1.5">
        <input
          className={number}
          data-testid={id}
          type="number"
          min={0}
          step="any"
          disabled={!editing}
          placeholder={f.empty ?? ''}
          value={shown}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (!raw) onChange(f.empty ? null : undefined);
            else {
              const n = Number(raw) * scale;
              onChange(f.path === 'budgets.cost_usd' ? n : Math.round(n));
            }
          }}
        />
        {f.unit ? <span className="text-xs text-page-muted">{f.unit}</span> : null}
      </span>
    );
  }
  return (
    <>
      <span className="text-page-text" title={f.help}>
        {f.label}
        <span className="block text-[11px] text-page-muted">{f.help}</span>
        {problems.map((p) => (
          <Problem key={p} text={p} />
        ))}
      </span>
      <span>{control}</span>
      <span className="flex items-center gap-1">
        <SourceTag source={source} changed={changed} />
        {editing && own !== undefined ? (
          <button
            type="button"
            className="text-sm text-page-muted bg-transparent border-0 cursor-pointer"
            data-testid={`${id}-reset`}
            title="Back to default"
            onClick={() => onChange(undefined)}
          >
            ↺
          </button>
        ) : null}
      </span>
      {extra}
    </>
  );
}

/**
 * Checks, as switches (ADR 0039 §3 verification): "a good answer must …" — suggested rules for this agent (no
 * tool error, use one of its app's read tools first) and the person's own (use a tool,
 * contain a text, be JSON matching a schema). A rule off is a rule not in the harness.
 */
function SimpleChecks({
  checks,
  tools,
  suggested,
  editing,
  onChecks,
}: {
  checks: Draft[];
  tools: string[];
  suggested: string[];
  editing: boolean;
  onChecks(c: Draft[]): void;
}) {
  const [adding, setAdding] = React.useState(false);
  const [kind, setKind] = React.useState<'tool_called' | 'answer_matches' | 'answer_json_schema'>(
    'tool_called',
  );
  const [tool, setTool] = React.useState(tools[0] ?? '');
  const [text, setText] = React.useState('');
  const [schema, setSchema] = React.useState('');
  const [bad, setBad] = React.useState('');
  const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);
  const has = (c: Draft) => checks.some((x) => same(x, c));
  const toggle = (c: Draft) => onChecks(has(c) ? checks.filter((x) => !same(x, c)) : [...checks, c]);
  const presets: { check: Draft; label: React.ReactNode }[] = [
    ...suggested.map((t) => ({
      check: { kind: 'tool_called', tool: t } as Draft,
      label: (
        <>
          The agent called <span className="font-mono text-xs">{t}</span> before answering
        </>
      ),
    })),
    { check: { kind: 'no_failed_tools' }, label: 'No tool failed' },
  ];
  const own = checks.filter((c) => !presets.some((p) => same(p.check, c)));
  const words = (c: Draft): React.ReactNode =>
    c.kind === 'tool_called' ? (
      <>
        The agent called <span className="font-mono text-xs">{String(c.tool)}</span>
      </>
    ) : c.kind === 'no_failed_tools' ? (
      'No tool failed'
    ) : c.kind === 'answer_matches' ? (
      `The answer contains “${String(c.pattern).replace(/\\(.)/g, '$1')}”`
    ) : c.kind === 'answer_json_schema' ? (
      'The answer is JSON matching a schema'
    ) : (
      String(c.kind)
    );
  const add = () => {
    setBad('');
    if (kind === 'tool_called') {
      if (!tool) return setBad('Pick a tool.');
      onChecks([...checks, { kind, tool }]);
    } else if (kind === 'answer_matches') {
      if (!text.trim()) return setBad('Write the text the answer must contain.');
      onChecks([
        ...checks,
        { kind, pattern: text.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags: 'i' },
      ]);
    } else {
      try {
        const parsed = JSON.parse(schema) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
        onChecks([...checks, { kind, schema: parsed as Draft }]);
      } catch {
        return setBad('The schema must be a JSON object.');
      }
    }
    setAdding(false);
    setText('');
    setSchema('');
  };
  return (
    <div data-testid="harness-checks">
      {presets.map((p, i) => (
        <SwitchRow
          editing={editing}
          key={i}
          on={has(p.check)}
          label={p.label}
          onToggle={() => toggle(p.check)}
          testId="harness-check-preset"
        />
      ))}
      {own.map((c, i) => (
        <SwitchRow
          editing={editing}
          key={`own-${i}`}
          on
          label={
            <>
              {words(c)} <span className="text-xs text-page-muted">· yours</span>
            </>
          }
          onToggle={() => onChecks(checks.filter((x) => !same(x, c)))}
          onRemove={() => onChecks(checks.filter((x) => !same(x, c)))}
          testId="harness-check"
        />
      ))}
      {editing && !adding ? (
        <button
          type="button"
          className="mt-1 text-xs text-nav-active bg-transparent border-0 p-0 cursor-pointer"
          data-testid="harness-add-check"
          onClick={() => setAdding(true)}
        >
          + Add a check
        </button>
      ) : null}
      {editing && adding ? (
        <div
          className="mt-2 rounded-md border border-nav-active/60 p-2 grid gap-2"
          data-testid="harness-own-rule"
        >
          <div className="text-xs text-page-muted">New check</div>
          <div className="grid grid-cols-[12rem_minmax(0,1fr)] gap-2">
            <select className={input} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="tool_called">The agent called</option>
              <option value="answer_matches">The answer contains</option>
              <option value="answer_json_schema">The answer is JSON matching</option>
            </select>
            {kind === 'tool_called' ? (
              <select className={input} value={tool} onChange={(e) => setTool(e.target.value)}>
                {tools.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            ) : kind === 'answer_matches' ? (
              <input
                className={input}
                placeholder="e.g. app_dashboard"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            ) : (
              <textarea
                className={input + ' font-mono text-xs'}
                rows={3}
                placeholder='{"type":"object","required":["screens"]}'
                value={schema}
                onChange={(e) => setSchema(e.target.value)}
              />
            )}
          </div>
          {bad ? <div className="text-xs text-red-600">{bad}</div> : null}
          <div className="flex gap-2 justify-end">
            <button type="button" className={btnGhost} onClick={() => (setAdding(false), setBad(''))}>
              Cancel
            </button>
            <button type="button" className={btnPrimary} data-testid="harness-own-rule-add" onClick={add}>
              Add
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const END_OPS = [
  { value: 'equals', label: 'equal' },
  { value: 'exists', label: 'exist' },
  { value: 'matches', label: 'match' },
  { value: 'gte', label: 'be at least' },
  { value: 'lte', label: 'be at most' },
];

/**
 * Checks on the real state (ADR 0039 §3 `verification.end_state`): after the answer the platform calls one of the
 * agent's read tools with a fixed input and checks a field of its result. Shown only where a manifest has one, and
 * not added from the page: the input is written ahead, so it cannot read what this run changed (§25 wants that).
 */
function EndStateChecks({
  checks,
  editing,
  onChecks,
}: {
  checks: Draft[];
  editing: boolean;
  onChecks(c: Draft[]): void;
}) {
  if (!checks.length) return null;
  const words = (c: Draft) =>
    `${c.path ? String(c.path) : 'the result'} must ${END_OPS.find((o) => o.value === c.op)?.label ?? String(c.op)}${
      c.op === 'exists' ? '' : ` ${typeof c.value === 'string' ? c.value : JSON.stringify(c.value)}`
    }`;
  return (
    <div className="mt-3 border-t border-divider pt-2" data-testid="harness-end-state">
      <div className="text-xs text-page-muted mb-1">
        <b className="text-page-text">Read back after the answer</b> — set in the manifest: the platform calls
        a read tool with a fixed input and checks a field of its result.
      </div>
      {checks.map((c, i) => (
        <div
          key={i}
          className="flex items-center gap-2 border-t border-divider first:border-t-0 py-1.5 text-sm"
          data-testid="harness-end-state-row"
        >
          <span className="font-mono text-xs">{String(c.tool)}</span>
          <span className="text-page-muted">→</span>
          <span className="text-page-text">{words(c)}</span>
          {editing ? (
            <button
              type="button"
              className="ml-auto text-page-muted bg-transparent border-0 cursor-pointer"
              aria-label="Remove the check"
              onClick={() => onChecks(checks.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** One rule of the checks: a switch, its words and (a rule of the person's own) a remove. */
function SwitchRow({
  on,
  label,
  editing,
  onToggle,
  onRemove,
  testId,
}: {
  on: boolean;
  label: React.ReactNode;
  editing: boolean;
  onToggle(): void;
  onRemove?: () => void;
  testId: string;
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-3 py-1.5 border-t border-divider first:border-t-0 text-sm',
        editing ? 'cursor-pointer' : '',
      )}
      data-testid={testId}
      data-on={String(on)}
    >
      <input
        type="checkbox"
        role="switch"
        className="sr-only peer"
        checked={on}
        disabled={!editing}
        onChange={onToggle}
      />
      <SwitchTrack on={on} dim={!editing} />
      <span className="text-page-text">{label}</span>
      {onRemove && editing ? (
        <button
          type="button"
          className="ml-auto text-page-muted bg-transparent border-0 cursor-pointer"
          aria-label="Remove the rule"
          onClick={(e) => (e.preventDefault(), onRemove())}
        >
          ✕
        </button>
      ) : null}
    </label>
  );
}

function flatten(v: unknown, prefix = ''): [string, unknown][] {
  if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length)
    return Object.entries(v as Draft).flatMap(([k, x]) => flatten(x, prefix ? `${prefix}.${k}` : k));
  return [[prefix, v]];
}

function EffectiveTable({ view, onlyChanged }: { view: HarnessView; onlyChanged: boolean }) {
  const fromDefault = new Set(view.fromDefault.map((d) => d.path));
  const rows = flatten(view.effective.harness).filter(
    ([p]) => !onlyChanged || [...fromDefault].some((d) => p === d || p.startsWith(`${d}.`)),
  );
  if (!rows.length)
    return (
      <div className="text-sm text-page-muted" data-testid="harness-effective-default">
        Every value is the platform default (how agents ran before the harness existed).
      </div>
    );
  return (
    <table className="w-full text-xs border-collapse" data-testid="harness-effective-table">
      <thead>
        <tr className="text-left text-page-muted">
          <th className="py-1 pr-3 font-normal">Setting</th>
          <th className="py-1 pr-3 font-normal">Value</th>
          <th className="py-1 pr-3 font-normal">Platform default</th>
          <th className="py-1 font-normal">Comes from</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([p, v]) => {
          const top = Object.keys(view.effective.sources).find((s) => p === s || p.startsWith(`${s}.`)) ?? p;
          return (
            <tr key={p} className="border-t border-divider" data-testid="harness-effective-row" data-path={p}>
              <td className="py-1 pr-3 font-mono">{p}</td>
              <td className="py-1 pr-3 font-mono">{showValue(v)}</td>
              <td className="py-1 pr-3 font-mono text-page-muted">{showValue(get(view.defaults, p))}</td>
              <td className="py-1">
                {SOURCE_LABEL[view.effective.sources[top] ?? 'platform'] ?? view.effective.sources[top]}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** A tab's one line on top: what the tab is, in the guide's words (§2, §43). */
export const TabLead = ({ children, testId }: { children: React.ReactNode; testId?: string }) => (
  <div className="text-sm text-page-muted" data-testid={testId}>
    {children}
  </div>
);

/** The Budgets tab (tab id `harness`): how the agent runs and when it must stop (§23–24, §29). */
export function HarnessTab(ctx: AgentTabContext) {
  return (
    <div className="flex flex-col gap-3" data-testid="harness-tab">
      <TabLead testId="budgets-lead">
        Budgets decide when the agent must stop: how long it may work and how many steps, calls and tokens it
        may use.
      </TabLead>
      <HarnessSections {...ctx} parts={['mode', 'budgets']} />
    </div>
  );
}
